/**
 * Schemas Zod compartidos FE ↔ BFF.
 *
 * Convención: cada schema exporta `<Nombre>Schema` y un type derivado
 * `<Nombre>Input` para usar en hooks/handlers. Inputs viajan por la red;
 * el BFF re-valida siempre (nunca confiar en validación cliente-side).
 */
import { z } from 'zod';

// Validators de formatos oficiales mexicanos (CURP, RFC, CLABE, INE,
// teléfono, email). Re-exportados desde el index para que las apps los
// importen directo desde @aosenuma/lib-validation.
export {
  validateCURP, normalizeCURP,
  validateRFC, normalizeRFC,
  validateCLABE, normalizeCLABE,
  validateClaveElector, normalizeClaveElector,
  validateTelefonoMx, normalizeTelefonoMx,
  validateEmail, normalizeEmail,
  validateFolioDerechos, validateFolioTierras, normalizeFolioRan,
  validateClaveCatastral, normalizeClaveCatastral,
  type ValidationResult,
} from './mxFormats';

// Helper para enmascarar el titular antes de persistirlo en
// `propietarios.titular_masked`. El BFF lo llama tanto en
// `seed_propietario_inventario` (RPC) como en `upsertPropietarioV2`
// para mantener el campo en sync sin exponer PII.
//
// `normalizeTitular` exporta la regla canónica UPPERCASE + NFC para que
// cualquier ruta (FE submit, BFF defensivo, scripts de seeding, fixtures
// e2e) escriba el cleartext con la misma forma. Sin esto, el mismo
// nombre tipeado distinto en dos sesiones cifraba a bytes distintos y
// rompía lookups por equality.
export { buildTitularMasked, normalizeTitular } from './titularMasked';

/* ============================== Identificadores ============================== */

export const Uuidv7Schema = z.string().uuid();

export const ParcelaIdSchema = z
  .string()
  // RAN: HUM-P#### / COR-P#### · Uso común (mig 0068): HUM-UC#### /
  // COR-UC#### · Privadas (handoff v2 + mig 0056):
  // PRV-<TENANT_SLUG_UPPER_2_5>-##### (ej. PRV-ELA-00001).
  // Si cambias este regex, sincroniza el espejo en
  // apps/api-edge/src/routes/captura.ts (GET /:parcela_id).
  .regex(
    /^((HUM|COR)-(P|UC)\d{4}|PRV-[A-Z]{2,5}-\d{5})$/i,
    'parcela_id inválido (esperado HUM-P####, COR-P####, HUM-UC####, COR-UC#### o PRV-XXX-#####)',
  );

export const TenantSlugSchema = z
  .string()
  .min(2)
  .max(40)
  .regex(/^[a-z0-9-]+$/, 'tenant slug solo lowercase, dígitos y guiones');

/* ============================== Comentarios (Elawan ↔ Aosenuma threads) ============================== */

export const ComentarioInputSchema = z.object({
  parcela_id: ParcelaIdSchema,
  cuerpo: z.string().min(1).max(2000),
  /**
   * UUIDv7 del comentario padre (root del hilo) si esto es una reply.
   * NULL/undefined = root nuevo. El BFF valida via trigger DB que el
   * parent pertenezca a la misma parcela y mismo tenant.
   */
  parent_id: Uuidv7Schema.optional().nullable(),
});
export type ComentarioInput = z.infer<typeof ComentarioInputSchema>;

/**
 * PATCH /v1/comentarios/:id — toggle de read_at y/o resuelto.
 * El BFF/RLS valida que solo el autor o staff puede tocar resuelto;
 * cualquier user del tenant puede marcar read_at en mensajes ajenos.
 */
export const ComentarioPatchInputSchema = z.object({
  /** true = setear read_at=now() · false = setear read_at=NULL · undefined = no cambio. */
  read: z.boolean().optional(),
  /** true = resuelto + resuelto_por + resuelto_at · false = revertir · undefined = no cambio. */
  resuelto: z.boolean().optional(),
}).refine((p) => p.read !== undefined || p.resuelto !== undefined, {
  message: 'Debe incluir al menos `read` o `resuelto`',
});
export type ComentarioPatchInput = z.infer<typeof ComentarioPatchInputSchema>;

/* ============================== Uploads (R2) ============================== */

export const BucketSchema = z.enum(['aosenuma-expedientes', 'aosenuma-evidencias']);

export const TipoDocSchema = z.enum([
  // Titularidad genérica (paraguas; los casos específicos siguen abajo)
  'acreditacion_titularidad_parcela',
  // Expedientes (PII alta)
  'ine', 'curp', 'rfc', 'comprobante_domicilio', 'acta_nacimiento', 'acta_matrimonio',
  'certificado_parcelario', 'constancia_derechos_ejidales', 'acta_asamblea',
  'constancia_no_adeudos', 'datos_bancarios',
  'documento_propiedad', 'boleta_predial',
  'contrato_arrendamiento', 'contrato_firmado',
  // Evidencias (no PII)
  'foto_parcela', 'foto_vista_aerea', 'foto_visita',
  // Genérico
  'otro',
]);

export const MimeImageSchema = z.enum([
  'image/jpeg', 'image/png', 'image/webp', 'image/heic',
]);
export const MimeDocSchema = z.enum([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword', 'application/vnd.ms-excel',
]);

export const UploadUrlInputSchema = z.object({
  parcela_id: ParcelaIdSchema,
  propietario_id: Uuidv7Schema.optional(),
  tipo_doc: TipoDocSchema,
  filename: z.string().min(1).max(255).regex(/^[^\/\\\x00]+$/, 'filename inválido'),
  mime: z.string().min(1).max(120),
  size_bytes: z
    .number()
    .int()
    .positive()
    .max(50 * 1024 * 1024, 'máximo 50 MiB'),
  bucket: BucketSchema,
});
export type UploadUrlInput = z.infer<typeof UploadUrlInputSchema>;

export const UploadCompleteInputSchema = z.object({
  parcela_id: ParcelaIdSchema,
  propietario_id: Uuidv7Schema.optional(),
  tipo_doc: TipoDocSchema,
  bucket: BucketSchema,
  r2_key: z.string().min(1).max(500),
  filename: z.string().min(1).max(255),
  mime: z.string().min(1).max(120),
  size_bytes: z.number().int().positive().max(50 * 1024 * 1024),
  sha256_hex: z.string().regex(/^[0-9a-f]{64}$/, 'sha256_hex debe ser 64 chars hex'),
});
export type UploadCompleteInput = z.infer<typeof UploadCompleteInputSchema>;

/* ============================== Captura (wizard 3 pasos) ============================== */

// 'comisariado' = órgano de representación del núcleo agrario, titular
// típico de las tierras de uso común (mig 0068, RF5). El CHECK espejo vive
// en propietarios.tipo; parcela_propietario.tipo no tiene CHECK (mig 0040).
export const TipoTenenciaSchema = z.enum(['ejidatario', 'comunero', 'posesionario', 'propietario', 'avecindado', 'comisariado']);

/**
 * Política de persistencia (mayo 2026 — actualizada): el técnico de campo
 * captura datos en cualquier orden, en cualquier estado de gestión. Si llenó
 * AL MENOS UN campo del propietario (nombre, teléfono, INE, CURP, RFC, email,
 * dirección, CLABE, folios, clave catastral), todo lo capturado se persiste.
 * Esto evita la pérdida de trabajo previa: antes, faltar el "Nombre completo"
 * dejaba huérfanos teléfonos/INE/etc. ya tipeados.
 *
 * `titular` ahora es opcional. El BFF cifra solo lo que llegó (NULL bytes
 * cifrados en columnas vacías) y `titular_masked` queda NULL hasta que el
 * técnico vuelva a editar y agregue el nombre. La UI de Expedientes muestra
 * "Sin titular registrado" en ese caso, pero los datos NO se pierden.
 */
export const PropietarioInputSchema = z
  .object({
    titular: z.string().min(2).max(200).optional().nullable(),
    telefono: z.string().min(7).max(20).optional().nullable(),
    email: z.string().email().optional().nullable(),
    curp: z.string().regex(/^[A-Z]{4}\d{6}[HM][A-Z]{5}[0-9A-Z]\d$/, 'CURP inválido').optional().nullable(),
    rfc: z.string().regex(/^[A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3}$/i, 'RFC inválido').optional().nullable(),
    ine_numero: z.string().min(5).max(40).optional().nullable(),
    direccion: z.string().min(5).max(500).optional().nullable(),
    clabe: z.string().regex(/^\d{18}$/, 'CLABE debe ser 18 dígitos').optional().nullable(),
    tipo: TipoTenenciaSchema.default('ejidatario'),
    /**
     * Folio RAN federal — formato `^[0-9]{2}(TM|FD)[0-9]{8}$` (12 chars).
     * 2 dígitos de entidad INEGI (Querétaro=22) + TM (Folio Tierras) o FD
     * (Folio Derechos) + 8 dígitos. Long fija. Se normaliza a uppercase.
     * Ejemplo válido para Querétaro: `22FD00059032` o `22TM00012345`.
     *
     * Ambos folios comparten la misma estructura porque el RAN usa el
     * sufijo TM/FD para diferenciar; aceptamos cualquiera de los dos en
     * cada campo y el RAN garantiza unicidad cross-tipo.
     */
    folio_derechos: z
      .string()
      .regex(
        /^[0-9]{2}(TM|FD)[0-9]{8}$/,
        'Folio derechos: 12 chars formato RAN — 2 dígitos entidad + TM/FD + 8 dígitos (ej. 22FD00059032)',
      )
      .optional()
      .nullable(),
    folio_tierras: z
      .string()
      .regex(
        /^[0-9]{2}(TM|FD)[0-9]{8}$/,
        'Folio tierras: 12 chars formato RAN — 2 dígitos entidad + TM/FD + 8 dígitos (ej. 22TM00012345)',
      )
      .optional()
      .nullable(),
    /**
     * Clave catastral municipal — sin estándar federal. Querétaro usa
     * ~13 alfanuméricos (ej. `F14C75I1349AD` para Corregidora). Aceptamos
     * 8–20 chars uppercase + alfanuméricos para cubrir variantes
     * municipales sin amarrar a un municipio específico.
     */
    clave_catastral: z
      .string()
      .regex(
        /^[A-Z0-9]{8,20}$/,
        'Clave catastral: 8–20 caracteres alfanuméricos (ej. F14C75I1349AD)',
      )
      .optional()
      .nullable(),
  });
// NOTA: anteriormente había un .refine() que requería "al menos UN campo
// sustantivo no-vacío". Removido 2026-05-15 porque bloqueaba el caso de
// borrado total de PII vía WYSIWYG: técnico con MFA borraba todos los
// campos → cliente saltaba envío para evitar el 400 → telefono_v2/email_v2
// /etc. quedaban con valores cifrados viejos en propietarios.
//
// Política actual: el cliente (CapturaPage handleSubmit) decide si mandar
// `propietario` basándose en estado real (parcela tiene propietario + PII
// descifrado tras MFA). Si llega payload con todos los campos null, es
// intención WYSIWYG de borrar — upsertPropietarioV2 ejecuta UPDATE SET
// *_v2 = NULL. Sin riesgo de payloads vacíos accidentales porque el
// cliente solo envía cuando el técnico interactuó con el form en estado
// descifrado.
export type PropietarioInput = z.infer<typeof PropietarioInputSchema>;

/** JSON en `gestiones.notas`; debe alojar extras + hasta 4 narrativas (~5k cada una). Postgres `text` es ilimitado. */
export const GESTION_NOTAS_JSON_MAX_CHARS = 32768;

export const NegociacionInputSchema = z.object({
  estado: z.enum(['sin_prospectar', 'en_valoracion', 'en_negociacion', 'contratada', 'inviable']),
  monto_renta_ha: z.number().nonnegative().max(1_000_000).optional().nullable(),
  plazo_anos: z.number().int().min(1).max(99).optional().nullable(),
  enganche_pct: z.number().min(0).max(100).optional().nullable(),
  notas: z.string().max(GESTION_NOTAS_JSON_MAX_CHARS).optional().nullable(),
  riesgo: z.enum(['bajo', 'medio', 'alto']).optional().nullable(),
});
export type NegociacionInput = z.infer<typeof NegociacionInputSchema>;

export const IrregularidadInputSchema = z.object({
  subtipo: z.string().min(2).max(80),
  gravedad: z.enum(['leve', 'media', 'alta', 'bloqueante']),
  descripcion: z.string().max(2000).optional().nullable(),
});
export type IrregularidadInput = z.infer<typeof IrregularidadInputSchema>;

export const CapturaInputSchema = z.object({
  parcela_id: ParcelaIdSchema,
  /** Datos del propietario. Opcional si el flujo solo actualiza gestion. */
  propietario: PropietarioInputSchema.optional(),
  negociacion: NegociacionInputSchema,
  irregularidades: z.array(IrregularidadInputSchema).max(20).default([]),
});
export type CapturaInput = z.infer<typeof CapturaInputSchema>;

// Parser puro de los XLSX heredados (LOS_MARTINEZ + SAN_ISIDRO 2026-05-08).
// Lo consume el script services/db/seeds/migrate_titulares_xlsx.ts.
export {
  classifyAndNormalizeRow,
  dropLiteralDuplicates,
  dedupTitulares,
  summarize,
  parseUbicacion,
  parseTipoPropiedad,
  type RawXlsxRow,
  type ClassifiedRow,
  type SendableRow,
  type SkippedRow,
  type DedupedPropietario,
  type ParserSummary,
  type TipoLink,
} from './migrateTitularesParser';
